import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogActions, Button, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { expertApi } from 'src/api/expertApi';
import ExpertModalForm from 'src/pages/Experts/components/ExpertModalForm';
import toast from 'react-hot-toast';

const normalizeText = (text) => {
  if (typeof text !== 'string') return text;
  return text.trim().replace(/\s+/g, ' ');
};

const cleanFormData = (data) => {
  const cleanedData = {};
  const textFields = ['name', 'email', 'position', 'company', 'bio', 'location', 'promptModify', 'hashtags'];
  Object.entries(data).forEach(([key, value]) => {
    // Skip empty strings, empty arrays, and null/undefined values
    if (value === '' || value === null || value === undefined) return;
    if (Array.isArray(value) && value.length === 0) return;

    // Normalize text fields
    if (textFields.includes(key) && typeof value === 'string') {
      const normalizedValue = normalizeText(value);
      if (normalizedValue) {
        cleanedData[key] = normalizedValue;
      }
      return;
    }

    // For pointOfContactJson, only include if it has valid entries and normalize text fields
    if (key === 'pointOfContactJson' && Array.isArray(value)) {
      const validContacts = value
        .map((contact) => ({
          ...contact,
          firstName: normalizeText(contact.firstName),
          lastName: normalizeText(contact.lastName),
          email: normalizeText(contact.email),
        }))
        .filter((contact) => contact.firstName || contact.lastName || contact.email);
      if (validContacts.length > 0) {
        cleanedData[key] = validContacts;
      }
      return;
    }

    // For reportsContactsJson, only include if it has valid entries and normalize text fields
    if (key === 'reportsContactsJson' && Array.isArray(value)) {
      const validContacts = value
        .map((contact) => ({
          ...contact,
          firstName: normalizeText(contact.firstName),
          lastName: normalizeText(contact.lastName),
          email: normalizeText(contact.email),
        }))
        .filter((contact) => contact.firstName || contact.lastName || contact.email);
      if (validContacts.length > 0) {
        cleanedData[key] = validContacts;
      }
      return;
    }

    cleanedData[key] = value;
  });
  return cleanedData;
};

const AddExpertModal = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    position: '',
    company: '',
    bio: '',
    hashtags: '',
    pointOfContactJson: [],
    reportsContactsJson: [],
    promptModify: '',
    location: 'Location not specified',
    imageURL: 'https://ucarecdn.com/52021bd1-5a2d-48f9-b7d3-6c0730fc039d/',
    qwotedURL: '',
    companyURL: '',
    linkedinURL: '',
    twitterURL: '',
    websiteURL: '',
    isActive: true,
  });

  const [isFormValid, setIsFormValid] = useState(false);

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      position: '',
      company: '',
      bio: '',
      hashtags: '',
      pointOfContactJson: [],
      reportsContactsJson: [],
      promptModify: '',
      location: 'Location not specified',
      imageURL: 'https://ucarecdn.com/52021bd1-5a2d-48f9-b7d3-6c0730fc039d/',
      qwotedURL: '',
      companyURL: '',
      linkedinURL: '',
      twitterURL: '',
      websiteURL: '',
      isActive: true,
    });
    setIsFormValid(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!isFormValid) return;

    try {
      const cleanedData = cleanFormData(formData);
      ['name', 'email', 'position', 'company', 'bio', 'location', 'promptModify', 'hashtags'].forEach((field) => {
        if (formData[field] !== cleanedData[field]) {
          // Field was normalized/cleaned
        }
      });

      if (formData.reportsContactsJson.length > 0) {
        formData.reportsContactsJson.forEach((originalContact, index) => {
          const cleanedContact = cleanedData.pointOfContactJson?.[index];
          if (cleanedContact) {
            ['firstName', 'lastName', 'email'].forEach((field) => {
              if (originalContact[field] !== cleanedContact[field]) {
                // Contact field was normalized/cleaned
              }
            });
          }
        });
      }

      await expertApi.createExpert(cleanedData);
      toast.success('Expert added successfully');
      handleClose();
    } catch (error) {
      toast.error('Failed to add expert: ' + error.message);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleValidationChange = (valid) => {
    setIsFormValid(valid);
  };

  const handleAddPoC = () => {
    setFormData((prev) => ({
      ...prev,
      pointOfContactJson: [...prev.pointOfContactJson, { id: Date.now(), firstName: '', lastName: '', email: '' }],
    }));
  };

  const handleRemovePoC = (id) => {
    setFormData((prev) => ({
      ...prev,
      pointOfContactJson: prev.pointOfContactJson.filter((poc) => poc.id !== id),
    }));
  };

  const handleUpdatePoC = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      pointOfContactJson: prev.pointOfContactJson.map((poc) => {
        if (poc.id === id) {
          return { ...poc, [field]: value };
        }
        return poc;
      }),
    }));
  };

  const handleAddReportContact = () => {
    setFormData((prev) => ({
      ...prev,
      reportsContactsJson: [...prev.reportsContactsJson, { id: Date.now(), firstName: '', lastName: '', email: '' }],
    }));
  };

  const handleRemoveReportContact = (id) => {
    setFormData((prev) => ({
      ...prev,
      reportsContactsJson: prev.reportsContactsJson.filter((contact) => contact.id !== id),
    }));
  };

  const handleUpdateReportContact = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      reportsContactsJson: prev.reportsContactsJson.map((contact) => {
        if (contact.id === id) {
          return { ...contact, [field]: value };
        }
        return contact;
      }),
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          margin: { xs: '8px', sm: '16px' },
          width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 32px)' },
          '& .MuiDialogContent-root': {
            padding: '24px',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Add New Expert
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <ExpertModalForm
        formData={formData}
        onChange={handleChange}
        onAddPoC={handleAddPoC}
        onRemovePoC={handleRemovePoC}
        onUpdatePoC={handleUpdatePoC}
        onAddReportContact={handleAddReportContact}
        onRemoveReportContact={handleRemoveReportContact}
        onUpdateReportContact={handleUpdateReportContact}
        onValidationChange={handleValidationChange}
      />

      <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!isFormValid}>
          Add Expert
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddExpertModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AddExpertModal;
